import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { captureAppLovinClickIds, captureClickIds, setUTMUserProperties } from '../utils/utm';
import { storeFbCookies } from '../components/funnelV3/analytics/funnelV3Analytics';
import { scheduleDeferredWork } from '../utils/scheduleDeferredWork';

interface AmplitudeContextType {
    trackEvent: (eventName: string, eventProperties?: Record<string, any>) => void;
    setUserId: (userId: string) => void;
    setUserProperties: (properties: Record<string, any>) => void;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(undefined);

interface AmplitudeProviderProps {
    apiKey: string;
    children: ReactNode;
}

const SESSION_REPLAY_SAMPLE_RATE = 0.2;
const SESSION_REPLAY_FALLBACK_MS = 15_000;
const SESSION_REPLAY_INTERACTION_DELAY_MS = 3000;

export const AmplitudeProvider: React.FC<AmplitudeProviderProps> = ({ apiKey, children }) => {
    useEffect(() => {
        // Keep core analytics immediate so attribution and early funnel events
        // are available without putting Session Replay on the critical path.
        amplitude.init(apiKey, undefined, {
            fetchRemoteConfig: true,
            autocapture: true,
        });
        // First-touch attribution: UTMs, click IDs (fbclid), and Meta cookies (_fbp/_fbc)
        setUTMUserProperties();
        captureClickIds();
        captureAppLovinClickIds();
        storeFbCookies();

        let cancelled = false;
        const cancelSchedule = scheduleDeferredWork(() => {
            void import('@amplitude/plugin-session-replay-browser')
                .then((sessionReplay) => {
                    if (cancelled) return;
                    amplitude.add(sessionReplay.plugin({
                        sampleRate: SESSION_REPLAY_SAMPLE_RATE,
                    }));
                })
                .catch(() => {
                    // Replay is optional and must never block the funnel.
                });
        }, SESSION_REPLAY_FALLBACK_MS, SESSION_REPLAY_INTERACTION_DELAY_MS);

        return () => {
            cancelled = true;
            cancelSchedule();
        };
    }, [apiKey]);

    const trackEvent = (eventName: string, eventProperties?: Record<string, any>) => {
        amplitude.track(eventName, eventProperties);
    };

    const setUserId = (userId: string) => {
        // Call the real setUserId so Amplitude merges anonymous events with this identity
        amplitude.setUserId(userId);

        const identify = new amplitude.Identify();
        identify.setOnce('first_identified_at', new Date().toISOString());
        amplitude.identify(identify);

        amplitude.flush();
    };

    const setUserProperties = (properties: Record<string, any>) => {
        const identify = new amplitude.Identify();
        Object.entries(properties).forEach(([key, value]) => {
            identify.set(key, value);
        });
        amplitude.identify(identify);
    };

    return (
        <AmplitudeContext.Provider value={{ trackEvent, setUserId, setUserProperties }}>
            {children}
        </AmplitudeContext.Provider>
    );
};

// Custom hook to use Amplitude
export const useAmplitude = (): AmplitudeContextType => {
    const context = useContext(AmplitudeContext);
    if (!context) {
        throw new Error('useAmplitude must be used within AmplitudeProvider');
    }
    return context;
};